// src/components/Layout/QuestsPanel.js
import React, { useState } from 'react';
import styles from './QuestsPanel.module.css';
import { ItemData } from '../../data/ItemData';
import { MonsterData } from '../../data/monsterData';
import { getItemImageSrc } from '../../utils/getItemImageSrc';

const QuestsPanel = ({ playerData, onCompleteQuest, onFulfillOrder, onSkipQuest, onSkipOrder, onPurchaseNewQuest, onPurchaseNewOrder }) => {
  const [showQuests, setShowQuests] = useState(true);
  const [showOrders, setShowOrders] = useState(true);
  const [expandedQuests, setExpandedQuests] = useState(new Set());
  const [expandedOrders, setExpandedOrders] = useState(new Set());

  // Quest/Order management pricing
  const getSkipCost = () => 50; // Fixed cost to skip
  const getBasePurchaseCost = (type) => type === 'quest' ? 100 : 150; // Base cost to purchase new quest/order
  
  const getPurchaseCost = (type) => {
    const dailyTracking = playerData.dailyQuestOrderManagement || { 
      questsPurchased: 0, 
      ordersPurchased: 0, 
      lastResetDay: playerData.currentDay || 1 
    };
    
    const baseCost = getBasePurchaseCost(type);
    const purchaseCount = type === 'quest' ? dailyTracking.questsPurchased : dailyTracking.ordersPurchased;
    return baseCost + (purchaseCount * baseCost * 0.5); // 50% increase per purchase
  };

  const canAffordSkip = () => playerData.gold >= getSkipCost();
  const canAffordPurchase = (type) => playerData.gold >= getPurchaseCost(type);

  const toggleQuestExpansion = (questId) => {
    const newExpanded = new Set(expandedQuests);
    if (newExpanded.has(questId)) {
      newExpanded.delete(questId);
    } else {
      newExpanded.add(questId);
    }
    setExpandedQuests(newExpanded);
  };

  const toggleOrderExpansion = (orderId) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const canCompleteQuest = (quest) => {
    if (quest.type === 'kill') {
      return (quest.currentAmount || 0) >= quest.requiredAmount;
    } else if (quest.type === 'collect') {
      return (playerData.inventory[quest.target] || 0) >= quest.requiredAmount;
    } else if (quest.type === 'mixed') {
      return quest.requirements.every(req => {
        if (req.type === 'kill') {
          return (req.currentAmount || 0) >= req.amount;
        } else if (req.type === 'collect') {
          return (playerData.inventory[req.target] || 0) >= req.amount;
        }
        return false;
      });
    }
    return false;
  };

  const canFulfillOrder = (order) => {
    for (const [itemKey, requiredQuantity] of Object.entries(order.requestedItems)) {
      const baseItem = ItemData[itemKey];
      if (!baseItem) return false;

      if (baseItem.stackable) {
        if ((playerData.inventory[itemKey] || 0) < requiredQuantity) return false;
      } else { 
        let foundCount = 0;
        for (const invItemId in playerData.inventory) {
          const invItem = playerData.inventory[invItemId];
          if (typeof invItem === 'object' && invItem.key === itemKey) {
            foundCount++;
          }
        }
        if (foundCount < requiredQuantity) return false;
      }
    }
    return true;
  };

  return (
    <div className={styles.questsPanelContainer}>
      <div className={styles.questsPanelHeader}>
        <h2 className={styles.questsPanelTitle}>Quests & Orders</h2>
      </div>
      
      <div className={styles.questsPanelContent}>
        {/* Quest Section */}
        {playerData.buildings?.questBoard?.owned ? (
          <div className={styles.section}>
            <button className={styles.sectionHeader} onClick={() => setShowQuests(!showQuests)}>
              Active Quests ({playerData.activeQuests.length})
              <span className={`${styles.arrow} ${showQuests ? styles.open : ''}`}>&#9660;</span>
            </button>
            {showQuests && (
              <div className={styles.list}>
                {playerData.activeQuests.length > 0 ? (
                  playerData.activeQuests.map(quest => {
                    const isExpanded = expandedQuests.has(quest.instanceId);
                    return (
                      <div key={quest.instanceId} className={styles.listItem}>
                        <div className={styles.itemHeader} onClick={() => toggleQuestExpansion(quest.instanceId)} style={{ cursor: 'pointer' }}>
                          <strong style={{ color: canCompleteQuest(quest) ? '#27ae60' : '#f1c40f' }}>{quest.title}</strong>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span>({quest.daysToComplete} days left)</span>
                            <span className={`${styles.arrow} ${isExpanded ? styles.open : ''}`}>&#9660;</span>
                          </div>
                        </div>
                        {isExpanded && (
                          <>
                            <p className={styles.itemDescription}>{quest.description}</p>
                            {quest.type === 'kill' && (
                              <p className={styles.itemProgress}>
                                Progress: {quest.currentAmount || 0} / {quest.requiredAmount} {MonsterData[quest.target]?.name || quest.target}
                              </p>
                            )}
                            {quest.type === 'collect' && (
                              <p className={styles.itemProgress}>
                                Need: {ItemData[quest.target]?.name || quest.target} x{quest.requiredAmount} (Have: {playerData.inventory[quest.target] || 0})
                              </p>
                            )}
                            {quest.type === 'mixed' && (
                              <div className={styles.itemProgress}>
                                <p>Requirements:</p>
                                {quest.requirements.map((req, index) => (
                                  <p key={index} style={{ fontSize: '0.75em', marginLeft: '8px' }}>
                                    {req.type === 'kill' 
                                      ? `• Defeat ${req.target}: ${req.currentAmount || 0}/${req.amount}`
                                      : `• Collect ${ItemData[req.target]?.name || req.target}: ${playerData.inventory[req.target] || 0}/${req.amount}`
                                    }
                                  </p>
                                ))}
                              </div>
                            )}
                            <p className={styles.itemReward}>Reward: {quest.rewardGold}G
                              {quest.xpGain && Object.entries(quest.xpGain).map(([skill, xp]) => ` & ${xp} ${skill} XP`).join('')}
                            </p>
                            <div className={styles.actionButtons}>
                              <button
                                onClick={() => onCompleteQuest(quest.instanceId)}
                                disabled={!canCompleteQuest(quest)}
                                className={styles.actionButton}
                              >
                                Complete Quest
                              </button>
                              <button
                                onClick={() => onSkipQuest(quest.instanceId)}
                                disabled={!canAffordSkip()}
                                className={`${styles.actionButton} ${styles.skipButton}`}
                                title={`Skip quest for ${getSkipCost()}G`}
                              >
                                Skip ({getSkipCost()}G)
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className={styles.emptyMessage}>No active quests. Check the Quest Board for new ones tomorrow!</p>
                )}
                {/* Purchase New Quest Section - Only show when less than 3 quests */}
                {playerData.activeQuests.length < 3 && (
                  <div className={styles.purchaseSection}>
                    <h4>Quest Management</h4>
                    <p className={styles.purchaseDescription}>
                      Purchase additional quests today. Cost increases with each purchase and resets tomorrow.
                    </p>
                    <button
                      onClick={() => onPurchaseNewQuest()}
                      disabled={!canAffordPurchase('quest')}
                      className={`${styles.actionButton} ${styles.purchaseButton}`}
                    >
                      Purchase New Quest ({getPurchaseCost('quest')}G)
                    </button>
                    <p className={styles.purchaseInfo}>
                      Today's purchases: {(playerData.dailyQuestOrderManagement?.questsPurchased || 0)}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.section}>
            <p className={styles.emptyMessage}>Purchase a <strong>Quest Board</strong> from the shop to accept quests.</p>
          </div>
        )}

        {/* Customer Order Section */}
        {playerData.buildings?.shopFront?.owned ? (
          <div className={styles.section}>
            <button className={styles.sectionHeader} onClick={() => setShowOrders(!showOrders)}>
              Customer Orders ({playerData.customerOrders.length})
              <span className={`${styles.arrow} ${showOrders ? styles.open : ''}`}>&#9660;</span>
            </button>
            {showOrders && (
              <div className={styles.list}>
                {playerData.customerOrders.length > 0 ? (
                  playerData.customerOrders.map(order => {
                    const isExpanded = expandedOrders.has(order.instanceId);
                    return (
                      <div key={order.instanceId} className={styles.listItem}>
                        <div className={styles.itemHeader} onClick={() => toggleOrderExpansion(order.instanceId)} style={{ cursor: 'pointer' }}>
                          <strong style={{ color: canFulfillOrder(order) ? '#27ae60' : '#f1c40f' }}>{order.customerName}</strong>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span>({order.daysToComplete} days left)</span>
                            <span className={`${styles.arrow} ${isExpanded ? styles.open : ''}`}>&#9660;</span>
                          </div>
                        </div>
                        {isExpanded && (
                          <>
                            <p className={styles.itemDescription}>Wants:</p>
                            <ul className={styles.requestedItemsList}>
                              {Object.entries(order.requestedItems).map(([itemKey, qty]) => (
                                <li key={itemKey}>
                                  <img src={getItemImageSrc(itemKey)} alt={ItemData[itemKey]?.name || itemKey} className={styles.requestedItemIcon}/>
                                  {ItemData[itemKey]?.name || itemKey} x{qty} 
                                  (Have: {ItemData[itemKey]?.stackable ? (playerData.inventory[itemKey] || 0) : Object.values(playerData.inventory).filter(i => typeof i === 'object' && i.key === itemKey).length})
                                </li>
                              ))}
                            </ul>
                            <p className={styles.itemReward}>Reward: {order.rewardGold}G
                                 {order.xpGain && Object.entries(order.xpGain).map(([skill, xp]) => ` & ${xp} ${skill} XP`).join('')}
                            </p>
                            <div className={styles.actionButtons}>
                              <button
                                onClick={() => onFulfillOrder(order.instanceId)}
                                disabled={!canFulfillOrder(order)}
                                className={styles.actionButton}
                              >
                                Fulfill Order
                              </button>
                              <button
                                onClick={() => onSkipOrder(order.instanceId)}
                                disabled={!canAffordSkip()}
                                className={`${styles.actionButton} ${styles.skipButton}`}
                                title={`Skip order for ${getSkipCost()}G`}
                              >
                                Skip ({getSkipCost()}G)
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className={styles.emptyMessage}>No customer orders currently. Check back tomorrow!</p>
                )}
                {/* Purchase New Order Section - Only show when less than 3 orders */}
                {playerData.customerOrders.length < 3 && (
                  <div className={styles.purchaseSection}>
                    <h4>Order Management</h4>
                    <p className={styles.purchaseDescription}>
                      Purchase additional customer orders today. Cost increases with each purchase and resets tomorrow.
                    </p>
                    <button
                      onClick={() => onPurchaseNewOrder()}
                      disabled={!canAffordPurchase('order')}
                      className={`${styles.actionButton} ${styles.purchaseButton}`}
                    >
                      Purchase New Order ({getPurchaseCost('order')}G)
                    </button>
                    <p className={styles.purchaseInfo}>
                      Today's purchases: {(playerData.dailyQuestOrderManagement?.ordersPurchased || 0)}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
           <div className={styles.section}>
            <p className={styles.emptyMessage}>Purchase a <strong>Shop Front</strong> from the shop to receive customer orders.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestsPanel; 